import React, { useRef, useState } from "react";
import { useCategories } from "../../../Hooks/useCategories";
import useCategoryNavigation, {
  NavMaterial,
} from "../../../Hooks/useCategoryNavigation";
import { NAV_ITEMS, NavItem } from "./navItems";
import { FiChevronDown } from "../../../icons";
import "../../../Styles/CategoryNav.css";

/**
 * Desktop primary navigation: a horizontal bar of material groups. Hovering a
 * group that has children opens a full-width mega-menu listing the API
 * categories (columns) and their subcategories.
 */
const CategoryNav: React.FC = () => {
  const { data } = useCategories();
  const { goToMaterial, goToCategory, goToSubcategory } =
    useCategoryNavigation();
  const [openMaterial, setOpenMaterial] = useState<NavMaterial | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const categories = data?.categories ?? [];

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpenMaterial(null), 120);
  };

  const openMenuFor = (item: NavItem) => {
    cancelClose();
    setOpenMaterial(item.hasMenu ? item.material : null);
  };

  const handleClickItem = (item: NavItem) => {
    setOpenMaterial(null);
    goToMaterial(item.material);
  };

  return (
    <nav
      className="cat-nav"
      onMouseLeave={scheduleClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpenMaterial(null);
      }}
    >
      <ul className="cat-nav-list">
        {NAV_ITEMS.map((item) => (
          <li
            key={item.label}
            className="cat-nav-item"
            onMouseEnter={() => openMenuFor(item)}
          >
            <button
              type="button"
              className={
                "cat-nav-link" +
                (openMaterial === item.material && item.hasMenu
                  ? " is-open"
                  : "")
              }
              onClick={() => handleClickItem(item)}
              aria-haspopup={item.hasMenu || undefined}
              aria-expanded={
                item.hasMenu ? openMaterial === item.material : undefined
              }
            >
              {item.label}
              {item.hasMenu && (
                <FiChevronDown className="cat-nav-caret" aria-hidden />
              )}
            </button>
          </li>
        ))}
      </ul>

      {openMaterial && categories.length > 0 && (
        <div
          className="cat-mega"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="cat-mega-inner">
            {categories.map((category) => (
              <div className="cat-mega-col" key={category.id}>
                <button
                  type="button"
                  className="cat-mega-heading"
                  onClick={() => goToCategory(openMaterial, category.id)}
                >
                  {category.category_name}
                </button>
                <ul className="cat-mega-sublist">
                  {category.subcategories.map((sub) => (
                    <li key={sub.subcategory_id ?? sub.subcategory_name}>
                      <button
                        type="button"
                        className="cat-mega-sublink"
                        onClick={() =>
                          goToSubcategory(
                            openMaterial,
                            category.id,
                            sub.subcategory_id
                          )
                        }
                      >
                        {sub.subcategory_name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default CategoryNav;
